import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Download, FileText, Scale, Wallet, Calculator, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const Guide = () => {
  const { t } = useApp();
  const [form, setForm] = useState({ name: "", email: "" });
  const [calc, setCalc] = useState({
    price: 2000000,
    rent: 140000,
    service: 18000,
    appreciation: 20,
  });

  const netYield = (((calc.rent - calc.service) / calc.price) * 100).toFixed(2);
  const totalReturn = (
    ((calc.rent - calc.service) / calc.price) * 100 +
    Number(calc.appreciation)
  ).toFixed(2);

  const handleDownload = (e) => {
    e.preventDefault();
    if (!form.email) return;
    toast.success("Guide sent! Check your inbox.");
    setForm({ name: "", email: "" });
  };

  const sections = [
    { Icon: FileText, title: t("guide_s1_title"), body: t("guide_s1_body") },
    { Icon: Scale, title: t("guide_s2_title"), body: t("guide_s2_body") },
    { Icon: Wallet, title: t("guide_s3_title"), body: t("guide_s3_body") },
    { Icon: Calculator, title: t("guide_s4_title"), body: t("guide_s4_body") },
    { Icon: ShieldAlert, title: t("guide_s5_title"), body: t("guide_s5_body") },
  ];

  return (
    <main data-testid="guide-page" className="bg-[#0A0E1A] pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-7">
            <p className="overline">{t("guide_overline")}</p>
            <h1 className="font-display text-4xl lg:text-7xl mt-4 leading-[1.04] text-[#F5F0E8]">
              {t("guide_title")}
            </h1>
            <p className="mt-6 text-lg text-[#A0A5B5] font-light leading-relaxed max-w-xl">
              {t("guide_sub")}
            </p>
          </div>
          {/* Lead magnet form */}
          <aside
            data-testid="guide-leadmagnet"
            className="lg:col-span-5 border border-[#C9A84C]/30 p-8 lg:p-10 bg-[#1E2235]/40"
          >
            <p className="overline">{t("guide_form_title")}</p>
            <p className="mt-3 text-[#A0A5B5] text-sm font-light">{t("guide_form_sub")}</p>
            <form onSubmit={handleDownload} className="mt-7 space-y-5">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("contact_name")}
                data-testid="guide-name-input"
                required
                className="lux-input"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("contact_email")}
                data-testid="guide-email-input"
                required
                className="lux-input"
              />
              <button
                type="submit"
                data-testid="guide-download-btn"
                className="btn-gold w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 text-xs tracking-[0.22em] uppercase font-medium"
              >
                <Download className="w-4 h-4" /> {t("guide_download")}
              </button>
            </form>
          </aside>
        </div>

        {/* Sections */}
        <div className="grid lg:grid-cols-2 gap-px bg-[#C9A84C]/15">
          {sections.map(({ Icon, title, body }, i) => (
            <div
              key={i}
              data-testid={`guide-section-${i}`}
              className="bg-[#0A0E1A] p-10 lg:p-14"
            >
              <div className="flex items-center gap-4">
                <span className="font-display italic text-[#C9A84C]/40 text-3xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Icon className="w-6 h-6 text-[#C9A84C]" strokeWidth={1.2} />
              </div>
              <h3 className="font-display text-3xl text-[#F5F0E8] mt-5 leading-tight">
                {title}
              </h3>
              <div className="gold-divider my-5" />
              <p className="text-[#A0A5B5] font-light leading-[1.85]">{body}</p>
            </div>
          ))}
        </div>

        {/* Calculator */}
        <section
          data-testid="roi-calculator"
          className="mt-20 border border-[#C9A84C]/25 p-8 lg:p-14"
        >
          <p className="overline">Tool</p>
          <h2 className="font-display text-3xl lg:text-5xl mt-3 text-[#F5F0E8]">
            {t("calc_title")}
          </h2>
          <div className="gold-divider my-8" />
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-7">
              {[
                ["price", t("calc_price")],
                ["rent", t("calc_rent")],
                ["service", t("calc_service")],
                ["appreciation", t("calc_appreciation")],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-[#A0A5B5]">
                    {label}
                  </label>
                  <input
                    type="number"
                    value={calc[k]}
                    onChange={(e) => setCalc({ ...calc, [k]: Number(e.target.value) })}
                    data-testid={`calc-input-${k}`}
                    className="lux-input mt-2"
                  />
                </div>
              ))}
            </div>
            <div className="lg:col-span-5 border border-[#C9A84C]/20 p-8 bg-[#1E2235]/40">
              <div className="mb-8">
                <p className="overline">{t("calc_net_yield")}</p>
                <p
                  data-testid="calc-net-yield"
                  className="font-display text-5xl text-[#C9A84C] mt-3"
                >
                  {netYield}%
                </p>
              </div>
              <div className="gold-divider my-6" />
              <div>
                <p className="overline">{t("calc_total_return")}</p>
                <p
                  data-testid="calc-total-return"
                  className="font-display text-5xl text-[#F5F0E8] mt-3"
                >
                  {totalReturn}%
                </p>
                <p className="mt-2 text-xs text-[#A0A5B5] italic">
                  Yield + appreciation by handover.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Guide;